import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import './RescheduleDialog.css';

export type RescheduleDialogProps = {
  visible: boolean;
  certId: string;
  initialDateTime: string | null;
  insuredDatabaseId: string;
  onHide: () => void;
  onSuccess: () => void;
};

const RescheduleDialog: React.FC<RescheduleDialogProps> = ({
  visible,
  certId,
  initialDateTime,
  insuredDatabaseId,
  onHide,
  onSuccess,
}) => {
  const [date, setDate] = React.useState<Date | null>(null);
  const [hour, setHour] = React.useState('12');
  const [minute, setMinute] = React.useState('00');
  const [period, setPeriod] = React.useState<'AM' | 'PM'>('AM');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Convert incoming UTC ISO string → local 12h components
  React.useEffect(() => {
    if (visible) {
      const dt = initialDateTime ? new Date(initialDateTime) : new Date();
      const localH = dt.getHours();
      const localM = dt.getMinutes();
      const p: 'AM' | 'PM' = localH >= 12 ? 'PM' : 'AM';
      const h12 = localH % 12 || 12;
      setDate(dt);
      setHour(h12.toString().padStart(2, '0'));
      setMinute(localM.toString().padStart(2, '0'));
      setPeriod(p);
      setError(null);
    }
  }, [visible, initialDateTime]);

  const handleHourBlur = () => {
    const h = parseInt(hour);
    setHour((!isNaN(h) ? Math.min(12, Math.max(1, h)) : 12).toString().padStart(2, '0'));
  };

  const handleMinuteBlur = () => {
    const m = parseInt(minute);
    setMinute((!isNaN(m) ? Math.min(59, Math.max(0, m)) : 0).toString().padStart(2, '0'));
  };

  const handleConfirm = async () => {
    if (!date) { setError('Please select a date.'); return; }
    const h = parseInt(hour);
    const m = parseInt(minute);
    if (isNaN(h) || h < 1 || h > 12) { setError('Hour must be between 1 and 12.'); return; }
    if (isNaN(m) || m < 0 || m > 59) { setError('Minute must be between 00 and 59.'); return; }

    let h24 = h;
    if (period === 'PM' && h !== 12) h24 += 12;
    if (period === 'AM' && h === 12) h24 = 0;

    const d = new Date(date);
    d.setHours(h24, m, 0, 0);

    // Convert local time → UTC for the API
    const pad = (n: number) => n.toString().padStart(2, '0');
    const newTime = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:00`;

    setSaving(true);
    setError(null);
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      const res = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/update-scheduled-datetime`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', NCToken: ncToken, Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ historicalCertificateHolderId: certId, newTime, insuredDatabaseId }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `Request failed [${res.status}]`);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to update schedule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={() => { if (!saving) onHide(); }}
      header={null}
      footer={null}
      closable={!saving}
      className="reschedDlg"
      modal
      draggable={false}
      resizable={false}
    >
      {/* Icon */}
      <div className="reschedDlg__iconWrap">
        <i className="pi pi-calendar reschedDlg__icon" />
      </div>

      <div className="reschedDlg__title">Reschedule Certificate</div>
      <div className="reschedDlg__subtitle">
        Select a new date and time for this scheduled certificate.
      </div>

      {/* Form */}
      <div className="reschedDlg__form">

        {/* Date */}
        <div>
          <label className="reschedDlg__label">Date</label>
          <div className="reschedDlg__calendarWrap">
            <Calendar
              value={date}
              onChange={e => setDate(e.value as Date)}
              dateFormat="mm/dd/yy"
              placeholder="Select date"
              showIcon
              minDate={new Date()}
            />
          </div>
        </div>

        {/* Time */}
        <div>
          <label className="reschedDlg__label">Time</label>
          <div className="reschedDlg__timeRow">

            <input
              type="text"
              inputMode="numeric"
              value={hour}
              onChange={e => setHour(e.target.value.replace(/\D/g, '').slice(0, 2))}
              onBlur={handleHourBlur}
              placeholder="HH"
              className="p-inputtext reschedDlg__timeInput"
            />

            <span className="reschedDlg__timeSep">:</span>

            <input
              type="text"
              inputMode="numeric"
              value={minute}
              onChange={e => setMinute(e.target.value.replace(/\D/g, '').slice(0, 2))}
              onBlur={handleMinuteBlur}
              placeholder="MM"
              className="p-inputtext reschedDlg__timeInput"
            />

            <div className="reschedDlg__ampm">
              {(['AM', 'PM'] as const).map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  className={`reschedDlg__ampmBtn${period === p ? ' reschedDlg__ampmBtn--active' : ''}`}
                >
                  {p}
                </button>
              ))}
            </div>

          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="reschedDlg__error">
            <i className="pi pi-exclamation-circle" style={{ fontSize: 12 }} />
            {error}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="reschedDlg__footer">
        <button
          type="button"
          className="p-button reschedDlg__cancelBtn"
          onClick={onHide}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="p-button reschedDlg__confirmBtn"
          onClick={handleConfirm}
          disabled={saving}
        >
          <i className={`pi ${saving ? 'pi-spin pi-spinner' : 'pi-check'}`} style={{ fontSize: 12 }} />
          {saving ? 'Saving…' : 'Update Schedule'}
        </button>
      </div>
    </Dialog>
  );
};

export default RescheduleDialog;
